import { useParams, Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import SEO from '@/components/SEO';
import { Opportunity } from "@/hooks/useOpportunitiesApi";
import { cn } from "@/lib/utils";

// Function to generate a consistent color based on a string
function stringToColor(str: string) {
  const colors = [
    "from-pink-500 to-rose-500",
    "from-purple-500 to-indigo-500",
    "from-blue-500 to-cyan-500",
    "from-emerald-500 to-green-500",
    "from-amber-500 to-yellow-500",
    "from-orange-500 to-red-500",
    "from-fuchsia-500 to-pink-500",
    "from-sky-500 to-blue-500",
    "from-teal-500 to-emerald-500",
    "from-lime-500 to-green-500",
  ];
  
  // Simple hash function to get a consistent index
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
}

function OpportunityCard({ opportunity, profile, session }: { opportunity: Opportunity, profile: any, session: any }) {
  const gradientClass = stringToColor(opportunity.name);
  
  return (
    <Link
      to={`/opportunities/${opportunity.id}`}
      className="block group"
    >
      <Card className="overflow-hidden h-full hover:shadow-lg transition-shadow rounded-xl">
        <div className="aspect-[4/3] relative overflow-hidden rounded-t-xl">
          {opportunity.image_url ? (
            <img
              src={opportunity.image_url}
              alt={opportunity.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className={cn(
              "w-full h-full flex items-center justify-center group-hover:scale-105 transition-transform duration-300",
              "bg-gradient-to-br", gradientClass
            )}>
              <span className="text-white text-2xl font-medium">
                {opportunity.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-lg mb-2 line-clamp-2">
            {opportunity.name}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-2">
            {opportunity.community_need}
          </p>
        </div>
      </Card>
    </Link>
  );
}

export default function NPOSkuOpportunitiesPage({ session, profile }: { session: any; profile: any }) {
  const { npoSlug } = useParams();
  const { ref, inView } = useInView();

  const fetchNPOOpportunities = async ({ pageParam = 1 }) => {
    const params = new URLSearchParams();
    params.append('page', pageParam.toString());
    params.append('limit', '9');
    params.append('npo', npoSlug || '');

    const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/opportunities/?${params.toString()}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ['npo-opportunities', npoSlug],
    queryFn: fetchNPOOpportunities,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      return pagination.page < pagination.totalPages ? pagination.page + 1 : undefined;
    },
    enabled: !!npoSlug,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage, isFetchingNextPage]);

  const opportunities = data?.pages.flatMap(page => page.data) || [];
  const npoName = opportunities[0]?.npo_name || npoSlug;

  if (status === "pending") {
    return (
      <div className="container mx-auto py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-[300px] w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="container mx-auto py-8">
        <Card className="p-6">
          <h1 className="text-2xl font-bold">Error</h1>
          <p className="text-gray-600 mt-2">
            {error instanceof Error ? error.message : "Failed to load opportunities"}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`${npoName} Volunteer Opportunities`}
        description={`Explore volunteer opportunities from ${npoName}`}
      />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <div className="text-sm text-gray-600 mb-4">
              <Link to="/opportunities" className="hover:text-blue-600">Opportunities</Link>
              <span className="mx-2">/</span>
              <span>{npoName}</span>
            </div>
            <h1 className="text-3xl font-bold">{npoName} Opportunities</h1>
            <p className="text-gray-600 mt-2">
              Explore volunteer opportunities from {npoName}
            </p>
          </div>

          {opportunities.length === 0 ? (
            <Card className="p-6">
              <h2 className="text-xl font-semibold">No opportunities found</h2>
              <p className="text-gray-600 mt-2">
                This organization doesn't have any active opportunities at the moment.
              </p>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {opportunities.map((opportunity) => (
                  <OpportunityCard key={opportunity.id} opportunity={opportunity} profile={profile} session={session} />
                ))}
              </div>

              <div ref={ref} className="flex justify-center mt-8 h-16">
                {isFetchingNextPage && (
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary"></div>
                    <p className="text-sm text-gray-500">Loading more...</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
